import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type CustomerRow } from '../../lib/api.js';

export function Customers() {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', contactEmail: '', domain: '', notes: '' });

  async function reload() {
    try {
      const res = await api.get<{ customers: CustomerRow[] }>('/api/admin/customers');
      setCustomers(res.customers);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/api/admin/customers', {
        name: form.name,
        ...(form.contactEmail ? { contactEmail: form.contactEmail } : {}),
        ...(form.domain ? { domain: form.domain } : {}),
        ...(form.notes ? { notes: form.notes } : {}),
      });
      setForm({ name: '', contactEmail: '', domain: '', notes: '' });
      setCreating(false);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div>
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">고객사</h1>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="px-3 py-1.5 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
        >
          {creating ? '취소' : '+ 새 고객사'}
        </button>
      </header>

      {creating && (
        <form
          onSubmit={create}
          className="mb-6 p-5 bg-surface border border-neutral-200 rounded-lg space-y-3"
        >
          <Input
            label="회사명 *"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <Input
            label="담당자 이메일"
            type="email"
            value={form.contactEmail}
            onChange={(v) => setForm({ ...form, contactEmail: v })}
          />
          <Input
            label="도메인"
            placeholder="acme.com"
            value={form.domain}
            onChange={(v) => setForm({ ...form, domain: v })}
          />
          <Input
            label="노트"
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
          <div className="pt-2">
            <button
              type="submit"
              className="px-4 py-2 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
            >
              저장
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-surface border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-medium">회사명</th>
              <th className="text-left px-4 py-2 font-medium">담당자</th>
              <th className="text-left px-4 py-2 font-medium">도메인</th>
              <th className="text-left px-4 py-2 font-medium">생성일</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {customers.map((c) => (
              <tr key={c.id} className="hover:bg-neutral-50/50">
                <td className="px-4 py-2 font-medium">
                  <Link to={`/admin/customers/${c.id}`} className="text-brand-600 hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-2 text-neutral-600">{c.contactEmail ?? '—'}</td>
                <td className="px-4 py-2 text-neutral-600 font-mono text-xs">{c.domain ?? '—'}</td>
                <td className="px-4 py-2 text-neutral-500 text-xs">
                  {new Date(c.createdAt).toLocaleDateString('ko-KR')}
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-neutral-500">
                  아직 등록된 고객사 없음
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-neutral-700 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
      />
    </label>
  );
}
